/**
 * ============================================================================
 * EVOLIS — module assistant IA (développement Louis, conçu pour être détachable)
 *  Dock flottant : chat en direct ouvert depuis le bouton de la sidebar.
 *  Permet de reprendre une conversation existante (historique partagé avec
 *  la page /evolis) et d'ouvrir la page complète.
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, History, Maximize2, X, MessageSquareText } from 'lucide-react';
import EvolisChat, { API_URL } from './EvolisChat';
import { pageCourante } from './contexte-pages';
import './evolis.css';

const EvolisDock = ({ onFermer }) => {
    const navigate = useNavigate();
    const location = useLocation();
    // Le dock sait sur quelle page il est ouvert : Evolis reçoit ce contexte
    // et l'écran d'accueil propose des questions adaptées à la page.
    const page = useMemo(() => pageCourante(location.pathname), [location.pathname]);
    const [conversationId, setConversationId] = useState(null);
    const [voirHistorique, setVoirHistorique] = useState(false);
    const [conversations, setConversations] = useState([]);
    // Remontage du chat uniquement sur « nouvelle conversation » (pas de flash
    // quand l'id arrive après le premier envoi).
    const [resetKey, setResetKey] = useState(0);

    const nouvelleConversation = () => {
        setConversationId(null);
        setResetKey((k) => k + 1);
        setVoirHistorique(false);
    };

    const chargerHistorique = useCallback(() => {
        axios.get(`${API_URL}/assistant/conversations`, { withCredentials: true })
            .then((res) => { if (res.data?.ok) setConversations(res.data.conversations); })
            .catch(() => { /* liste indisponible : le panneau reste vide */ });
    }, []);

    useEffect(() => { if (voirHistorique) chargerHistorique(); }, [voirHistorique, chargerHistorique]);

    const reprendre = (id) => {
        setConversationId(id);
        setVoirHistorique(false);
    };

    const extras = (
        <>
            <button className="evolis-dock-btn" title="Nouvelle conversation" onClick={nouvelleConversation}>
                <Plus size={14} />
            </button>
            <button
                className={`evolis-dock-btn${voirHistorique ? ' active' : ''}`}
                title="Reprendre une conversation"
                onClick={() => setVoirHistorique((v) => !v)}
            >
                <History size={14} />
            </button>
            <button className="evolis-dock-btn" title="Ouvrir la page Evolis" onClick={() => { onFermer(); navigate('/evolis'); }}>
                <Maximize2 size={14} />
            </button>
            <button className="evolis-dock-btn" title="Fermer" onClick={onFermer}>
                <X size={14} />
            </button>
        </>
    );

    return (
        <div className="evolis-dock">
            <EvolisChat
                key={resetKey}
                conversationId={conversationId}
                onConversation={setConversationId}
                compact
                contextePage={page.label || 'Dock Evolis (chat flottant)'}
                suggestions={page.suggestions}
                enteteExtras={extras}
            />

            {voirHistorique && (
                <div className="evolis-dock-historique">
                    <div className="evolis-dock-historique-titre">Reprendre une conversation</div>
                    {conversations.length === 0 && (
                        <div className="evolis-conv-vide">
                            <MessageSquareText size={18} />
                            <span>Aucune conversation sauvegardée</span>
                        </div>
                    )}
                    {conversations.map((c) => (
                        <button key={c.id} className="evolis-conv-item" onClick={() => reprendre(c.id)} title={c.titre}>
                            <span className="evolis-conv-titre">{c.titre}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default EvolisDock;
